import { type FC, useState } from 'react'
import { Radio } from '@/components/commons/radio'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { XCloseIcon } from '@/components/ui/icons'
import { Modal } from '@/components/ui/modal'
import {
	ModalBody,
	ModalFooter,
	ModalHeader
} from '@/components/ui/modal-atoms'
import { Separator } from '@/components/ui/separator'
import { useRejectPhotoset } from '@/features/photoset/queries/use-reject-photoset'
import type { DeclineReason } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'

interface RejectPhotosetModalProps {
	reasons: DeclineReason[]
	close: () => void
}

export const RejectPhotosetModal: FC<RejectPhotosetModalProps> = ({
	close,
	reasons
}) => {
	const [selectedReason, setSelectedReason] = useState<
		DeclineReason['id'] | null
	>(null)
	const rejectPhotoset = useRejectPhotoset({
		onError: (error) => errorHandler(error)
	})

	const { t } = useTranslation()

	const onCloseModal = () => {
		if (!rejectPhotoset.isPending) {
			close()
		}
	}

	return (
		<Modal onOverlayClick={onCloseModal} width={480}>
			<ModalHeader>
				<ButtonLegacy
					onClick={onCloseModal}
					shape='square'
					size='sm'
					type='button'
					variant='tertiary'
				>
					<XCloseIcon />
				</ButtonLegacy>
				<h3>{t('photoset:reject_modal.header_title')}</h3>
			</ModalHeader>

			<Separator orientation='horizontal' />

			<ModalBody className='flex-col gap-6 p-6 text-neutral-900'>
				<div className='flex flex-col gap-2'>
					<h1 className='font-medium text-xl'>
						{t('photoset:reject_modal.title')}
					</h1>
					<h2 className='text-sm'>
						{t('photoset:reject_modal.subtitle')}
					</h2>
				</div>

				<div className='flex flex-col gap-4'>
					{reasons.map((reason) => (
						<Radio
							key={reason.id}
							label={t(
								`photoset:reasons.${reason.key_name}` as TKey
							)}
							name='reason'
							onChange={() => setSelectedReason(reason.id)}
							value={reason.id}
						/>
					))}
				</div>
			</ModalBody>

			<ModalFooter>
				<ButtonLegacy
					onClick={() => close()}
					type='button'
					variant='tertiary'
				>
					{t('common:close')}
				</ButtonLegacy>
				<ButtonBrandLegacy
					disabled={!selectedReason || rejectPhotoset.isPending}
					onClick={() =>
						selectedReason && rejectPhotoset.mutate(selectedReason)
					}
				>
					{t('common:reject')}
				</ButtonBrandLegacy>
			</ModalFooter>
		</Modal>
	)
}
